import axios from 'axios';
import FuseUtils from '@fuse/utils/FuseUtils';
import apiUser from 'app/api/apiUser';
import {
   itemsInitialValues,
   projectsInitialValues,
   companiesInitialValues,
   posInitialValues,
   posInitialValuesClient,
   shipmentsInitialValues,
   diaryInitialValues,
   packagesInitialValues,
} from 'app/utils/UserSettingsDataGridInitialValues';
import { setStorageValue } from 'app/utils/UseLocalStorage';
import jwtServiceConfig from './jwtServiceConfig';

/* eslint-disable camelcase */

class JwtService extends FuseUtils.EventEmitter {
   hasLoggedOut = false;

   init() {
      this.setInterceptors();
      this.handleAuthentication();
   }

   /* I'm not using this setInterceptors */
   setInterceptors = () => {
      axios.interceptors.response.use(
         (response) => {
            return response;
         },
         (err) => {
            return new Promise((resolve, reject) => {
               if (err.response.status === 401 && err.config && !err.config.__isRetryRequest) {
                  // if you ever get an unauthorized response, logout the user
                  this.emit('onAutoLogout', 'Invalid access_token');
                  this.setSession(null);
               }
               throw err;
            });
         }
      );
   };

   /*
   handleAuthentication = () => {
      const access_token = this.getAccessToken();

      if (!access_token) {
         this.emit('onNoAccessToken');

         return;
      }

      if (this.isAuthTokenValid(access_token)) {
         this.setSession(access_token);
         this.emit('onAutoLogin', true);
      } else {
         this.setSession(null);
         this.emit('onAutoLogout', 'access_token expired');
      }
   };
   */

   handleAuthentication = async () => {
      try {
         const user = await apiUser.apiUser.current();

         const userFinal = this.createUserSettingsJWT(user);
         this.setStorage(userFinal);
         this.emit('onAutoLogin', userFinal);
      } catch (error) {
         if (error?.status === 401 || error?.response?.status === 401) {
            this.emit('onNoAccessToken');
         } else {
            this.emit('onNoAccessToken');
         }
      }
   };

   handleTokenExpired = () => {
      if (!this.hasLoggedOut) {
         this.hasLoggedOut = true;
         this.logout();
         this.emit('onAutoLogout', 'Your session has expired. Please log in again.');
      }
   };

   createUser = (data) => {
      return new Promise((resolve, reject) => {
         axios.post(jwtServiceConfig.signUp, data).then((response) => {
            if (response.data.user) {
               // this.setSession(response.data.access_token);
               resolve(response.data.user);
               this.emit('onLogin', response.data.user);
            } else {
               reject(response.data.error);
            }
         });
      });
   };

   signInWithEmailAndPassword = (username, password) => {
      return new Promise((resolve, reject) => {
         const data = {
            password,
            username,
         };
         apiUser.apiUser
            .login(data)
            .then((response) => {
               if (response) {
                  // this.setSession(response.token);
                  this.hasLoggedOut = false;
                  const userFinal = this.createUserSettingsJWT(response);
                  this.setStorage(userFinal);
                  this.emit('onLogin', userFinal);
                  resolve(userFinal);
               } else {
                  this.logout();
                  const rawErrors = response?.data?.errors;
                  const err = new Error('API error');
                  err.details = Array.isArray(rawErrors) ? rawErrors : [{ type: 'general', message: rawErrors || 'Unknown error during login.' }];
                  reject(err);
               }
            })
            .catch((err) => {
               this.logout();

               if (err?.details) {
                  reject(err); // <== importante: não faz Array.from aqui
               } else if (err?.response?.data?.errors) {
                  const rawErrors = err.response.data.errors;
                  const error = new Error('API error');
                  error.details = Array.isArray(rawErrors) ? rawErrors : [{ type: 'general', message: rawErrors || 'Unexpected error.' }];
                  reject(error);
               } else {
                  const error = new Error(err.message || 'Unexpected error.');
                  error.details = [{ type: 'general', message: error.message }];
                  reject(error);
               }
            });
      });
   };

   signInWithPasswordExpired = (username, password, newpassword) => {
      return new Promise((resolve, reject) => {
         const data = {
            password,
            username,
            newpassword,
         };
         apiUser.apiUser
            .loginexpired(data)
            .then((response) => {
               if (response) {
                  // this.setSession(response.token);
                  const userFinal = this.createUserSettingsJWT(response);
                  this.setStorage(userFinal);
                  localStorage.setItem('hasSession', 'true');
                  this.emit('onLogin', userFinal);
               } else {
                  this.logout();
                  const rawErrors = response?.data?.errors;
                  const err = new Error('API error');
                  err.details = Array.isArray(rawErrors) ? rawErrors : [{ type: 'general', message: rawErrors || 'Unknown error during login.' }];
                  reject(err);
               }
            })
            .catch((err) => {
               this.logout();
               if (err?.details) {
                  reject(err); // <== importante: não faz Array.from aqui
               } else if (err?.response?.data?.errors) {
                  const rawErrors = err.response.data.errors;
                  const error = new Error('API error');
                  error.details = Array.isArray(rawErrors) ? rawErrors : [{ type: 'general', message: rawErrors || 'Unexpected error.' }];
                  reject(error);
               } else {
                  const error = new Error(err.message || 'Unexpected error.');
                  error.details = [{ type: 'general', message: error.message }];
                  reject(error);
               }
            });
      });
   };

   signInWithToken = async () => {
      try {
         const user = await apiUser.apiUser.current();

         if (user) {
            this.hasLoggedOut = false;
            const userFinal = this.createUserSettingsJWT(user);
            this.setStorage(userFinal);
            this.emit('onLogin', userFinal);
            return userFinal;
         }
         this.handleTokenExpired();
         return null;
      } catch (error) {
         if (error?.status === 401 || error?.response?.status === 401) {
            this.handleTokenExpired();
         } else {
            this.logout();
         }

         throw new Error('Failed to login with token.');
      }
   };

   updateUserData = (user) => {
      return axios.post(jwtServiceConfig.updateUser, {
         user,
      });
   };

   setStorage = (userFinal) => {
      setStorageValue(`${userFinal.id}_items`, userFinal.data.itemsCols);
      setStorageValue(`${userFinal.id}_projects`, userFinal.data.projectsCols);
      setStorageValue(`${userFinal.id}_companies`, userFinal.data.companiesCols);
      setStorageValue(`${userFinal.id}_pos`, userFinal.data.posCols);
      setStorageValue(`${userFinal.id}_shipments`, userFinal.data.shipmentsCols);
      setStorageValue(`${userFinal.id}_historyNavigation`, userFinal.data.historyNavigation);
      setStorageValue(`${userFinal.id}_diary`, userFinal.data.diaryCols);
      setStorageValue(`${userFinal.id}_packages`, userFinal.data.packagesCols);
   };

   /*
   setSession = (access_token) => {
      if (access_token) {
         localStorage.setItem('jwt_access_token', access_token);
         axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
      } else {
         localStorage.removeItem('jwt_access_token');
         delete axios.defaults.headers.common.Authorization;
      }
   }; */

   logout = () => {
      this.hasLoggedOut = false; // ← garante que pode logar novamente depois
      localStorage.removeItem('hasSession');
      apiUser.apiUser.logout();
      this.emit('onLogout', 'Logged out');
   };

   /* isAuthTokenValid = (access_token) => {
      if (!access_token) {
         return false;
      }
      const decoded = jwtDecode(access_token);
      const currentTime = Date.now() / 1000;
      if (decoded.exp < currentTime) {
         console.warn('access token expired');
         return false;
      }

      return true;
   };

   getAccessToken = () => {
      return window.localStorage.getItem('jwt_access_token');
   };
   */

   /* Priscila -  I created this one to map */
   createUserSettingsJWT = (authuser) => {
      const user = {
         role: authuser.role,
         from: 'ait.api',
         id: authuser.id,
         loginRedirectUrl: getInitialUrl(authuser.role),
         data: {
            bWelcome: authuser.bWelcome, // temporary
            iCompanyId: authuser.iCompanyId,
            userName: authuser.userName,
            bNotShowDoc: authuser.bNotShowDoc,
            bEditCompany: authuser.bEditCompany,
            displayName: authuser.displayName,
            regionalDataFormat: authuser.regionalFormatData,
            timeZone: authuser.timeZone,
            email: authuser.email,
            photoURL: 'assets/images/avatars/profile.jpg',
            settings: '',
            itemsCols: [...itemsInitialValues],
            diaryCols: [...diaryInitialValues],
            projectsCols: [...projectsInitialValues],
            companiesCols: [...companiesInitialValues],
            posCols: authuser.role === 'client' ? [...posInitialValuesClient] : [...posInitialValues],
            shipmentsCols: [...shipmentsInitialValues],
            packagesCols: [...packagesInitialValues],
            historyNavigation: [],
            projectUserAccess: authuser.projectUserAccess || [],
            reportsAvailable: [],
         },
      };

      return user;
   };
}

const getInitialUrl = (authuser) => {
   switch (authuser) {
      case 'client':
         return '/client/dashboards/projects';
      case 'externalAgent':
         return '/agent/onshoreShipments';
      default:
         return '/operator/diarytask';
   }
};
const instance = new JwtService();

export default instance;
